// API keys tab — the (fixed) successor of src/modules/api-key/ApiKeysTab:
// create sends the required {name, preset} body via a dialog, the freshly
// minted secret is shown once with a copy affordance, the list is paged and
// searchable, and every row carries what an admin auditing access wants to
// know: which preset (or custom scope set) the key holds and when it last
// authenticated a request. Delete confirms — a key cannot be re-minted.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, Copy, Trash2, Loader2, Plus, Search as SearchIcon } from 'lucide-react';
import { toast } from 'sonner';
import moment from 'moment';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api, ep } from '../lib/api.js';
import { usePagedList } from '../lib/usePagedList.js';
import { ConfirmDialog } from './ConfirmDialog.jsx';
import { ListPagination } from './ListPagination.jsx';

// The backend's scope presets (app/auth/scopes.py); there is no public
// preset-catalogue endpoint, so the three values are pinned here. Labels are
// i18n KEYS resolved at render — a module constant cannot hold translated text.
const PRESETS = [
  { value: 'viewer', labelKey: 'apiKeys.presets.viewer' },
  { value: 'editor', labelKey: 'apiKeys.presets.editor' },
  { value: 'full', labelKey: 'apiKeys.presets.full' },
];

function fmt(value) {
  return value ? moment(value).format('DD MMM YYYY HH:mm') : '—';
}

// Keys minted before presets existed carry the wildcard `*` — every scope,
// not a "custom set of one".
function presetLabel(key, t) {
  const scopes = key.scopes || [];
  if (key.preset) return t(`apiKeys.presets.${key.preset}`);
  if (scopes.length === 1 && scopes[0] === '*') return t('apiKeys.presets.wildcard');
  return t('apiKeys.presets.custom', { count: scopes.length });
}

export const ApiKeysTab = ({ projectId }) => {
  // Strings identical to the legacy `apiKey` namespace are reused from it
  // (`apiKey:` / `common:` prefixes) instead of being duplicated here.
  const { t } = useTranslation('console');
  const list = usePagedList(ep.apiKeys(), { size: 25, params: { project_id: projectId } });
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [preset, setPreset] = useState('full');
  const [createdSecret, setCreatedSecret] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error(t('apiKeys.toasts.nameRequired'));
      return;
    }
    setCreating(true);
    const { success, response, message } = await api.post(ep.apiKeys(), {
      params: { project_id: projectId },
      data: { name: name.trim(), preset },
    });
    setCreating(false);
    if (!success) {
      toast.error(message);
      return;
    }
    setCreateOpen(false);
    setName('');
    setCreatedSecret(response);
    toast.success(t('apiKeys.toasts.created'));
    list.reload();
  };

  const handleDelete = async (key) => {
    const { success, message } = await api.del(ep.apiKeys(), {
      params: { api_key: key.id, project_id: projectId },
    });
    if (success) {
      toast.success(t('apiKeys.toasts.deleted'));
      list.reload({ silent: true });
    } else {
      toast.error(message);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t('apiKeys.toasts.copied'));
  };

  const keys = list.items;

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="relative flex-1 min-w-56 max-w-md">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={list.searchInput}
            onChange={(e) => list.setSearchInput(e.target.value)}
            placeholder={t('apiKeys.searchPlaceholder')}
            className="pl-9"
          />
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-2 text-white font-medium rounded-lg px-[21px] py-[12px] h-auto cursor-pointer"
          style={{ background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)' }}
        >
          <KeyRound className="w-4 h-4" />
          {t('apiKey:apiKeysTab.createButton')}
        </Button>
      </div>

      {list.loading && keys.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 text-blue-500 dark:text-blue-400 animate-spin" />
        </div>
      ) : keys.length > 0 ? (
        <div className="space-y-3">
          <div className="w-full border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('apiKeys.table.name')}</TableHead>
                  <TableHead>{t('apiKeys.table.keyPrefix')}</TableHead>
                  <TableHead>{t('apiKeys.table.preset')}</TableHead>
                  <TableHead className="text-center">{t('apiKeys.table.created')}</TableHead>
                  <TableHead className="text-center">{t('apiKeys.table.lastUsed')}</TableHead>
                  <TableHead className="text-center">{t('apiKey:apiKeysTab.table.action')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell className="font-medium">{key.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-muted-foreground">{key.key_prefix}…</span>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copy(key.key_prefix)}>
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span
                        className="inline-flex items-center rounded-full border bg-muted/50 px-2 py-0.5 text-xs text-foreground"
                        title={(key.scopes || []).join(', ')}
                      >
                        {presetLabel(key, t)}
                      </span>
                    </TableCell>
                    <TableCell className="text-center text-sm text-muted-foreground">{fmt(key.created_at)}</TableCell>
                    <TableCell className="text-center text-sm text-muted-foreground" title={key.last_used_at ? moment(key.last_used_at).fromNow() : undefined}>
                      {key.last_used_at ? fmt(key.last_used_at) : t('apiKeys.neverUsed')}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button size="sm" variant="destructive" onClick={() => setPendingDelete(key)} className="h-8" aria-label={t('apiKeys.delete.title')}>
                        <Trash2 className="h-4 w-4" color="#fff" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <ListPagination list={list} />
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <KeyRound className="h-16 w-16 mb-4 text-muted-foreground/60" />
          <p className="text-lg font-medium">{list.hasFilters ? t('list.noMatch') : t('apiKey:apiKeysTab.empty.title')}</p>
          <p className="text-sm text-muted-foreground">{list.hasFilters ? t('list.noMatchHint') : t('apiKeys.empty.description')}</p>
          {list.hasFilters && <Button variant="ghost" size="sm" className="mt-2" onClick={list.clearFilters}>{t('list.clearFilters')}</Button>}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('apiKey:apiKeysTab.createButton')}</DialogTitle>
            <DialogDescription>{t('apiKeys.create.description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="api-key-name">{t('apiKeys.create.nameLabel')}</Label>
              <Input
                id="api-key-name"
                placeholder={t('apiKeys.create.namePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('apiKeys.create.presetLabel')}</Label>
              <Select value={preset} onValueChange={setPreset}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRESETS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {t(p.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              {t('common:cancel')}
            </Button>
            <Button onClick={handleCreate} disabled={creating || !name.trim()}>
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {t('apiKeys.create.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Secret shown once */}
      <Dialog open={!!createdSecret} onOpenChange={(open) => !open && setCreatedSecret(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('apiKeys.secret.title')}</DialogTitle>
            <DialogDescription>{t('apiKeys.secret.description')}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 bg-muted/50 border rounded-lg p-3">
            <code className="font-mono text-sm break-all flex-1">
              {createdSecret?.secret_key}
            </code>
            <Button variant="outline" size="icon" className="shrink-0" onClick={() => copy(createdSecret?.secret_key)}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setCreatedSecret(null)}>{t('apiKeys.secret.done')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(o) => { if (!o) setPendingDelete(null); }}
        title={t('apiKeys.delete.title')}
        description={t('apiKeys.delete.body', { name: pendingDelete?.name ?? '' })}
        confirmLabel={t('apiKeys.delete.confirm')}
        destructive
        onConfirm={() => handleDelete(pendingDelete)}
      />
    </>
  );
};
